import React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import useUserCourseMaterials from "../hooks/useUserCourseMaterials";
import useCalendarExport from "../hooks/useCalendarExport";
import useCourseDays from "../hooks/useCourseDays";
import { FaCalendarAlt } from "react-icons/fa";
import { MonoButtonSmall } from "../kits";


// Hilfsfunktion zum Parsen von "14.6.2025" oder "14.06.2025"
function parseGermanDateString(str) {
  if (!str) return null;
  const [day, month, year] = str.split(".");
  return new Date(
    parseInt(year, 10),
    parseInt(month, 10) - 1,
    parseInt(day, 10)
  );
}

// Hilfsfunktion für sichere JSON-Parse
function safeJson(str, fallback = []) {
  try {
    return JSON.parse(str);
  } catch {
    return fallback;
  }
}



  






// Die Haupt-Komponente
const CourseCockpitSection = () => {
  // Holt alles Wichtige aus der URL!
  const { courseId } = useParams();
  const [search] = useSearchParams();
  const userId = search.get("user");
  const registrationId = search.get("registration");
  const courseTitle = search.get("title");
  const formattedDate = search.get("date");



// Hook um Kalender-Export-Funktionen zu nutzen
const { downloadICS, getGoogleCalendarLink } = useCalendarExport();

  // Daten laden
  const { materials, loading, error } = useUserCourseMaterials(userId, courseId);
  const {
    days = [],
    loading: daysLoading,
    error: daysError,
  } = useCourseDays(registrationId || 0);

  if (loading) return <p>Lade Kursmaterialien...</p>;
  if (error) return <p>Fehler: {error}</p>;
  if (!materials) return <p>Keine Inhalte verfügbar.</p>;

  const videos = safeJson(materials.video_links, []);
  const dayPlan = safeJson(materials.day_plan_json, {});

  // Priorität: Query → materials → Fallback
  const shownTitle = courseTitle || materials.course_title || "";
  const shownDate =
    formattedDate ||
    materials.custom_date ||
    materials.start_date ||
    "Noch nicht festgelegt";

  // Für Anzeige als Date-Objekt parsen
  const parsedDate =
    shownDate !== "Noch nicht festgelegt" && shownDate.includes(".")
      ? parseGermanDateString(shownDate)
      : shownDate !== "Noch nicht festgelegt"
      ? new Date(shownDate)
      : null;





  return (
    <>
      {shownTitle && (
        <h2 className="text-sm bg-gray-900 text-white pt-1 pb-1 pl-1 pr-1">{shownTitle}</h2>
      )}
      <p className="text-xs font-semibold px-2 pt-2 pb-2 rounded-md">
        Start:{" "}
        {parsedDate
          ? parsedDate.toLocaleDateString("de-DE")
          : shownDate}
      </p>

      {/* Kurstage */}
      <div>
        <span className="text-xs uppercase bg-gray-950 text-orange-300 px-2 pt-1 pb-1 pl-1 pr-1">
          Deine Kurstage:
        </span>
        {daysLoading ? (
          <p>Lade Kurstage...</p>
        ) : daysError ? (
          <p>Fehler: {daysError}</p>
        ) : days.length > 0 ? (
          <div className="space-y-2 mt-2">
            {days.map((d) => {
              // Auch Kurstag-Datum ggf. parsen!
              const eventDate =
                typeof d.date === "string" && d.date.includes(".")
                  ? parseGermanDateString(d.date)
                  : new Date(d.date);
              return (
                <div
                  key={d.id}
                  className="border border-black rounded-md p-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="text-black">
                    <FaCalendarAlt className="inline mr-2" />
                    <span className="bg-orange-300 text-gray-900 px-2 py-0.5 font-mono text-xs">
                      {eventDate.toLocaleDateString("de-DE")}
                    </span>{" "}
                    – <span className="text-xs">{d.title}</span>
                  </div>
                  <div className="flex gap-4 text-xs">
                   <MonoButtonSmall
                   className="bg-gray-950 text-white text-xs hover:bg-orange-300 hover:text-black"
                            onClick={() => downloadICS({
                              title: "After Effects Social Media",
                              date: new Date("2025-06-14T10:00:00"),  // oder dein Date-Objekt!
                              location: "Microsoft Teams",
                              description: "Dein Kurstag mit Denis Bitter"
                            })}
                          >
                            Outlook/Apple/Teams
                  </MonoButtonSmall>
                  
                  <MonoButtonSmall
                  className="bg-gray-950 text-white text-xs hover:bg-orange-300 hover:text-black"
                  >
                            <a
                              href={getGoogleCalendarLink({
                                title: "After Effects Social Media",
                                date: new Date("2025-06-14T10:00:00"),
                                location: "Microsoft Teams",
                                description: "Dein Kurstag mit Denis Bitter"
                              })}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Google
                            </a>
                  </MonoButtonSmall>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-700 text-xs pl-2 mt-3">
            Kurstage werden gerade zusammen gestellt. <br />Du bekommst ein E-Mail
          </p>
        )}
      </div>

      {/* Teams Link, Notizen, Videos, Tagesplan */}
      <div className="mt-5"></div>
      <div className="space-y-10 font-mono text-sm py-6 px-3 bg-gray-50 rounded-xl border border-black shadow-xl">
        {/* Teams Link */}
        <span className="text-xs uppercase text-white bg-gray-950 px-2 pt-1 pb-1 pl-1 pr-1">
          Kurs Teams Link:
        </span>
        <br />
        <span>
          <a
            href={materials.teams_link}
            target="_blank"
            rel="noreferrer"
            className="underline underline-offset-2 text-black pt-3"
          >
            <span className="text-xs pt-3 px-1">{materials.teams_link}</span>
          </a>
        </span>

        {/* Notizen */}
        <div className="pt-5">
          <span className="text-xs uppercase text-white bg-gray-950 px-2 pt-1 pb-1 pl-1 pr-1 ">
            Kurs Notizen:
          </span>
          <br />
          <span className="text-xs px-1 pt-2">{materials.personal_notes}</span>
        </div>

        {/* Videos */}
        <div>
          <span className="text-xs uppercase text-white bg-gray-950 px-2 pt-1 pb-1 pl-1 pr-1 ">
            Kurs Videos:
          </span>
          {videos.length > 0 ? (
            <ul className="list-disc pl-5 mt-1">
              {videos.map((v, i) => (
                <li key={v || i}>
                  <a
                    href={v}
                    target="_blank"
                    rel="noreferrer"
                    className="underline underline-offset-2 text-black"
                  >
                    {v}
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-400 px-1 pt-2 text-xs">Keine Videos vorhanden.</p>
          )}
        </div>

        {/* Tagesplan */}
        <div>
          <span className="text-xs uppercase text-white bg-gray-950 px-2 pt-1 pb-1 pl-1 pr-1">
            Kurs Cockpit Plan:
          </span>
          {Object.keys(dayPlan).length > 0 ? (
            <ul className="list-disc pl-5 mt-1">
              {Object.entries(dayPlan).map(([day, topics]) => (
                <li key={day}>
                  <strong>{day}</strong>
                  <ul className="list-disc pl-5">
                    {Array.isArray(topics) ? (
                      topics.map((t, i) => (
                        <li key={`${t.time}-${t.topic}-${i}`}>
                          {t.time} – {t.topic}: {t.details}
                        </li>
                      ))
                    ) : (
                      <li>{topics.toString()}</li>
                    )}
                  </ul>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-400 pt-2 text-xs px-1">Kein Tagesplan verfügbar.</p>
          )}
        </div>
      </div>
    </>
  );
};

export default CourseCockpitSection;
